export const vehicleStatusOptions = [
  'Pending Available',
  'Available',
  'Pending Sale',
  'Active Deal',
  'Sold',
  'Sold elsewhere',
]

export const vehicleTypeOptions = ['Step van', 'Cargo van', 'Cutaway', 'Box truck']

export const vehicleFuelOptions = ['Gas', 'Diesel']

/** Body manufacturers used for parts compatibility. */
export const partBodyManufacturerOptions = [
  'Morgan Olson',
  'Utilimaster',
  'Unicell',
  'Rockport',
  'Forest River',
  'Bay Bridge',
] as const

/** Common commercial-truck engine options for parts compatibility. */
export const vehicleEngineOptions = [
  '6.0L V8',
  '7.3L Powerstroke',
] as const

/** Newest model year users may enter (calendar year + 1 for next-model-year trucks). */
export const getMaxVehicleYear = (now = new Date()): number => now.getFullYear() + 1

export const getVehicleYearOptions = (minYear = 1990): string[] => {
  const maxYear = getMaxVehicleYear()
  const years: string[] = []
  for (let year = maxYear; year >= minYear; year -= 1) {
    years.push(String(year))
  }
  return years
}

export const dealQualityOptions = ['great', 'good', 'average', 'belowAverage', 'poor']

// Interface for status color mapping
export interface StatusColorConfig {
  hex: string;
  color: string;
  backgroundColor: string;
  selectedBackground: string;
  hoverBackground: string;
}

// Color mapping for vehicle statuses
export const vehicleStatusColorMap: Record<string, StatusColorConfig> = {
  'Available': {
    hex: '#2e7d32', // Dark green
    color: 'success',
    backgroundColor: 'rgba(46, 125, 50, 0.1)',
    selectedBackground: 'rgba(46, 125, 50, 0.2)',
    hoverBackground: 'rgba(46, 125, 50, 0.3)',
  },
  'available': {
    hex: '#2e7d32', // Dark green (legacy support)
    color: 'success',
    backgroundColor: 'rgba(46, 125, 50, 0.1)',
    selectedBackground: 'rgba(46, 125, 50, 0.2)',
    hoverBackground: 'rgba(46, 125, 50, 0.3)',
  },
  'Pending Available': {
    hex: '#1976d2', // Blue
    color: 'info',
    backgroundColor: 'rgba(2, 136, 209, 0.1)',
    selectedBackground: 'rgba(2, 136, 209, 0.2)',
    hoverBackground: 'rgba(2, 136, 209, 0.3)',
  },
  'Sold': {
    hex: '#d32f2f', // Dark red
    color: 'error',
    backgroundColor: 'rgba(211, 47, 47, 0.1)',
    selectedBackground: 'rgba(211, 47, 47, 0.2)',
    hoverBackground: 'rgba(211, 47, 47, 0.3)',
  },
  'sold': {
    hex: '#d32f2f', // Dark red (legacy support)
    color: 'error',
    backgroundColor: 'rgba(211, 47, 47, 0.1)',
    selectedBackground: 'rgba(211, 47, 47, 0.2)',
    hoverBackground: 'rgba(211, 47, 47, 0.3)',
  },
  'Pending Sale': {
    hex: '#ef5350', // Light red
    color: 'warning',
    backgroundColor: 'rgba(237, 108, 2, 0.1)',
    selectedBackground: 'rgba(237, 108, 2, 0.2)',
    hoverBackground: 'rgba(237, 108, 2, 0.3)',
  },
  'Pending': { // For backward compatibility
    hex: '#ef5350', // Light red
    color: 'warning',
    backgroundColor: 'rgba(237, 108, 2, 0.1)',
    selectedBackground: 'rgba(237, 108, 2, 0.2)',
    hoverBackground: 'rgba(237, 108, 2, 0.3)',
  },
  'Sold elsewhere': {
    hex: '#ef5350', // Light red
    color: 'error',
    backgroundColor: 'rgba(211, 47, 47, 0.1)',
    selectedBackground: 'rgba(211, 47, 47, 0.2)',
    hoverBackground: 'rgba(211, 47, 47, 0.3)',
  },
  'Reserved': {
    hex: '#9c27b0', // Purple
    color: 'secondary',
    backgroundColor: 'rgba(156, 39, 176, 0.1)',
    selectedBackground: 'rgba(156, 39, 176, 0.2)',
    hoverBackground: 'rgba(156, 39, 176, 0.3)',
  },
  'Active Deal': {
    hex: '#ff9800', // Orange
    color: 'warning',
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
    selectedBackground: 'rgba(255, 152, 0, 0.2)',
    hoverBackground: 'rgba(255, 152, 0, 0.3)',
  },
  'default': {
    hex: '#757575', // Gray
    color: 'default',
    backgroundColor: 'transparent',
    selectedBackground: 'action.selected',
    hoverBackground: 'action.hover',
  }
}

export const getVehicleSizeOptions = (vehicleType: string) => {
  if (!vehicleType) return []

  switch (vehicleType.toLowerCase()) {
    case 'step van':
      return ['P500', 'P700', 'P900', 'P1000', 'P1100', 'P1200', 'P1300']
    case 'cargo van':
      return ['low-standard', 'medium-standard', 'high-standard', 'low-extended', 'medium-extended', 'high-extended', 'long', 'short']
    case 'cutaway':
      return ['10ft', '12ft', '14ft', '15ft', '16ft', '17ft', '18ft']
    case 'box truck':
      return ['12ft', '14ft', '16ft', '18ft', '20ft', '22ft', '24ft', '26ft']
    default:
      return []
  }
}

const uniqueFtSizesSorted = (): string[] => {
  const ftSizes = [
    ...getVehicleSizeOptions('Cutaway'),
    ...getVehicleSizeOptions('Box truck'),
  ]
  return [...new Set(ftSizes)].sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
}

/** Row order for inventory PDF exports: P sizes, then ft sizes (numeric), then cargo van sizes */
export const INVENTORY_PDF_SIZE_SORT_ORDER: readonly string[] = [
  ...getVehicleSizeOptions('Step van'),
  ...uniqueFtSizesSorted(),
  'low-standard',
  'medium-standard',
  'high-standard',
  'low-extended',
  'medium-extended',
  'high-extended',
  'short',
  'long',
]

export const normalizeInventorySize = (size: string | null | undefined): string =>
  (size ?? '').trim().toLowerCase().replace(/\s+/g, '-')

const UNKNOWN_SIZE_SORT_BASE = INVENTORY_PDF_SIZE_SORT_ORDER.length

export const getInventorySizeSortIndex = (size: string | null | undefined): number => {
  const normalized = normalizeInventorySize(size)
  if (!normalized) return UNKNOWN_SIZE_SORT_BASE + 1

  const index = INVENTORY_PDF_SIZE_SORT_ORDER.findIndex(
    known => normalizeInventorySize(known) === normalized,
  )
  return index >= 0 ? index : UNKNOWN_SIZE_SORT_BASE
}

/** Compare sizes for PDF row ordering (known sizes first, then unknown/empty) */
export const compareInventorySizesForPdf = (
  sizeA: string | null | undefined,
  sizeB: string | null | undefined,
): number => {
  const indexA = getInventorySizeSortIndex(sizeA)
  const indexB = getInventorySizeSortIndex(sizeB)
  if (indexA !== indexB) return indexA - indexB

  const normA = normalizeInventorySize(sizeA)
  const normB = normalizeInventorySize(sizeB)
  if (!normA && !normB) return 0
  if (!normA) return 1
  if (!normB) return -1
  return normA.localeCompare(normB)
}

/** All type+size combinations for notification filtering (vehicle added). Format: "Type|Size" */
export const VEHICLE_SIZE_FILTER_OPTIONS: Array<{ type: string; size: string; label: string; value: string }> = (
  vehicleTypeOptions.flatMap((type) =>
    getVehicleSizeOptions(type).map((size) => ({
      type,
      size,
      label: `${type} - ${size}`,
      value: `${type}|${size}`,
    }))
  )
)

export const conditionOptions = ['New', 'Used']

export const makeOptions = ['Ford', 'Chevrolet', 'Freightliner', 'GMC', 'Mercedes-Benz', 'Isuzu', 'Hino']

export const getVehicleModelOptions = (vehicleMake: string) => {
  switch (vehicleMake) {
    case 'Ford':
      return ['E350', 'E450', 'F59', 'F650', 'F750', 'Transit', 'Transit T150', 'Transit T250', 'Transit T350', 'Transit 350 Velocity', 'Transit Connect'] // Ford Transit for backwards compatibility
    case 'Chevrolet':
      return ['Express 3500', 'Express 4500', '5500XD LCF']
    case 'Freightliner':
      return ['M2 106', 'MT45', 'MT55']
    case 'GMC':
      return ['Savana 3500', 'Savana 4500']
    case 'Mercedes-Benz':
      return ['Sprinter 1500','Sprinter 2500', 'Sprinter 3500']
    case 'Isuzu':
      return ['NLR', 'NPR', 'NQR', 'NRR', 'FRR', 'FSR', 'FTR', 'FVR']
    case 'Hino':
      return ['268', '268A', '338', 'L6', 'L7', 'XL7', 'XL8']
    default:
      return []
  }
}

export const getVehicleModelOptionsByType = (vehicleMake: string, vehicleType: string) => {
  if (!vehicleMake || !vehicleType) return []
  
  const makeLC = vehicleMake.toLowerCase()
  const typeLC = vehicleType.toLowerCase()
  
  if (makeLC === 'ford') {
    switch (typeLC) {
      case 'step van':
        return ['E350', 'E450', 'F59']
      case 'box truck':
        return ['F650', 'F750']
      case 'cutaway':
        return ['Transit', 'E350', 'E450']
      case 'cargo van':
        return ['Transit', 'Transit 350 Velocity']
      default:
        return []
    }
  }
  else if (makeLC === 'chevrolet') {
    switch (typeLC) {
      case 'cutaway':
        return ['Express 3500', 'Express 4500']
      case 'box truck':
        return ['Express 3500', 'Express 4500', '5500XD LCF']
      default:
        return []
    }
  }
  else if (makeLC === 'freightliner') {
    switch (typeLC) {
      case 'step van':
        return ['MT45', 'MT55']
      case 'box truck':
        return ['M2 106']
      default:
        return []
    }
  }
  else if (makeLC === 'gmc') {
    switch (typeLC) {
      case 'cutaway':
        return ['Savana 3500', 'Savana 4500']
      case 'box truck':
        return ['Savana 3500', 'Savana 4500']
      default:
        return []
    }
  }
  else if (makeLC === 'mercedes-benz') {
    switch (typeLC) {
      case 'cargo van':
        return ['Sprinter 1500', 'Sprinter 2500', 'Sprinter 3500', 'Metris']
      case 'cutaway':
        return ['Sprinter 1500', 'Sprinter 2500', 'Sprinter 3500']
      default:
        return []
    }
  }
  else if (makeLC === 'hino') {
    switch (typeLC) {
      case 'box truck':
        return ['268', '268A', '338', 'L6', 'L7', 'XL7', 'XL8']
      default:
        return []
    }
  }
}

/**
 * Get vehicle model options for multiple vehicle types
 * This function accepts arrays of makes and types and returns all possible model options
 * 
 * @param vehicleMakes - Array of vehicle makes
 * @param vehicleTypes - Array of vehicle types
 * @returns Array of unique model options
 */
export const getVehicleModelOptionsForMultipleTypes = (
  vehicleMakes: string[],
  vehicleTypes: string[]
): string[] => {
  if (!vehicleMakes || !vehicleMakes.length || !vehicleTypes || !vehicleTypes.length) {
    return [];
  }
  
  // Collect all models for each make-type combination
  const allModels: string[] = [];
  
  for (const make of vehicleMakes) {
    if (make === 'Any') continue; // Skip 'Any' option
    
    for (const type of vehicleTypes) {
      if (type === 'Any') continue; // Skip 'Any' option
      
      // Get models for this specific make-type combination
      const models = getVehicleModelOptionsByType(make, type) || [];
      allModels.push(...models);
    }
  }
  
  // Remove duplicates and return
  return [...new Set(allModels)];
};

/** All distinct inventory size values across vehicle types. */
export const getAllVehicleSizeOptions = (): string[] => [...INVENTORY_PDF_SIZE_SORT_ORDER]

/** All vehicle models across supported makes. */
export const getAllVehicleModelOptions = (): string[] =>
  [...new Set(makeOptions.flatMap(make => getVehicleModelOptions(make)))]